import { BackofficePrestationType, Prisma } from "@prisma/client";
import {
  ComputablePricing,
  safeComputablePricingValues,
  simplePricingValues,
} from "./pricing.types";

export type PromotionPrestationDataType = {
  days: number;
};

export type BackofficePrestationDataType = Record<string, never> | PromotionPrestationDataType;

export const isPromotionPrestationDataType = (
  data: unknown,
  options?: {
    daysNullable?: boolean;
  },
): data is PromotionPrestationDataType => {
  return (
    data !== null &&
    typeof data === "object" &&
    "days" in data &&
    (typeof data["days"] === "number" ||
      ((options?.daysNullable ?? false) && data["days"] === null))
  );
};

export const isBackofficePrestationDataType = (
  data: unknown,
): data is BackofficePrestationDataType => {
  if (data === null || typeof data !== "object") return false;
  return isPromotionPrestationDataType(data) || Object.keys(data).length === 0;
};

export const isBackofficePrestationType = (type: string): type is BackofficePrestationType =>
  Object.values(BackofficePrestationType).includes(type as BackofficePrestationType);

export const exhibitorProfilesPackNumberEnums = [1, 2, 3] as const;

export type ExhibitorProfilesPackNumber = (typeof exhibitorProfilesPackNumberEnums)[number];

export const isExhibitorProfilesPackNumber = (
  number: number,
): number is ExhibitorProfilesPackNumber =>
  exhibitorProfilesPackNumberEnums.includes(number as ExhibitorProfilesPackNumber);

export const exhibitorProfilesPackPrestationTypeEnums = [
  "exhibitorProfilesPack1Credits",
  "exhibitorProfilesPack2Credits",
  "exhibitorProfilesPack3Credits",
] as const;

export type ExhibitorProfilesPackPrestationType = Extract<
  BackofficePrestationType,
  (typeof exhibitorProfilesPackPrestationTypeEnums)[number]
>;

export const isExhibitorProfilesPackPrestationType = (
  type: BackofficePrestationType | string,
): type is ExhibitorProfilesPackPrestationType =>
  exhibitorProfilesPackPrestationTypeEnums.includes(type as ExhibitorProfilesPackPrestationType);

export const backofficePrestationValues =
  Prisma.validator<Prisma.BackofficePrestationDefaultArgs>()({
    include: {
      pricing: safeComputablePricingValues,
    },
  });

export type BackofficePrestation = Prisma.BackofficePrestationGetPayload<
  typeof backofficePrestationValues
>;

export const simplePrestationValues = Prisma.validator<Prisma.BackofficePrestationDefaultArgs>()({
  select: {
    type: true,
    name: true,
    data: true,
    pricing: simplePricingValues,
  },
});

export type SimplePrestation = Prisma.BackofficePrestationGetPayload<typeof simplePrestationValues>;

export type GetPrestations = Record<BackofficePrestationType, BackofficePrestation>;

export type PrestationWithPricing = {
  type: BackofficePrestationType;
  name: string;
  data: BackofficePrestationDataType | null;
  pricing: ComputablePricing | null;
};
